import React from 'react';
import CompareArrowsIcon from '@material-ui/icons/CompareArrows';
import { getAsLongDateTime, getAsStartAndEndTimeFormat, getAsLongDate } from '../../../../../common-client2/src/services/ukgDateTime';
import { ShiftSegmentTypeName, ListViewRequestTypes } from '../../../../Enums';
import { hasTransfer } from '../../../../auxilaries';
import i18n from '../../../../../i18n';

const tFixed = i18n.getFixedT(null, 'common');

const NON_REQUEST_CALENDAR_IDS = ["0", "1"];
export const getSegmentType = (type) => {
    switch(type){
        case ShiftSegmentTypeName.TRANSFER_SEGMENT:
            return (
                <div className="segment-type-transfer">
                    <CompareArrowsIcon fontSize='small' className="transfer-icon" />
                    <span className="segment-type">Transfer</span>
                </div>);
        case ShiftSegmentTypeName.REGULAR_SEGMENT:
            return <span className="segment-type">Regular</span>;
        case ShiftSegmentTypeName.BREAK_SEGMENT:
            return <span className="segment-type">Break</span>;
        default:
            return;

    }
}

export const toHours = (milliseconds) => milliseconds/1000/60/60;

const displayDurationInHours = (segment) => `[${(new Date(segment.endDateTime) - new Date(segment.startDateTime)) / 1000 / 60 / 60}]`;
export const getSegmentPeriod = (x) => `${getAsStartAndEndTimeFormat(x.startDateTime, x.endDateTime)} ${displayDurationInHours(x)}`;
export const getSegmentPeriodSearchIndex = (x) => `${getSegmentPeriod(x)}${tFixed(`SegmentType.${x.type}`)}`;

/**
 * @typedef {{
 *  id: number;
 *  listViewId: string;
 *  managerSettingId?: number;
 *  requestName: string;
 *  date: Date;
 *  dateLabel: string;
 *  type: typeof import('../../../../Enums/RequestType').RequestTypes;
 *  subtype: typeof import('../../../../Enums/RequestSubType').RequestSubtypes;
 *  owner: string?;
 *  location: string;
 *  job: string;
 *  shift: *;
 *  partialShift: * | null;
 *  isPartial: boolean;
 *  isMyRequest: boolean;
 *  searchIndex: string;
 *  isCheckable: boolean;
 * }} IListViewItem
 * @param {*[]} monthlySchedule 
 * @returns {IListViewItem[]} 
 */
export const toListView = (monthlySchedule) => {
    const monthlyScheduleListItems = monthlySchedule.map(x => {
        const { offeredShift, originalShift } = getOfferedShift(x);
        const startDate = offeredShift.startDateTime;
        const type = getType(x);
        const workSegment = getWorkSegment(type, offeredShift);
        const requestName = getRequestName(x);
        const dateLabel = getAsLongDateTime(startDate);
        const owner = getOwner(x);
        var listItem = {
            id: x.id,
            listViewId: `${x.id}_${x.managerSettingId}`,
            managerSettingId: x.managerSettingId,
            requestName: requestName,
            date: new Date(startDate),
            dateLabel: dateLabel,
            type: type,
            subtype: getSubtype(x),
            owner: owner,
            location: getLocation(workSegment?.shortOrgJobPath),
            job: getJob(workSegment?.shortOrgJobPath),
            shift: originalShift,
            partialShift: x.raw.partialShift,
            isPartial: x.raw.isPartial,
            isCheckable: ListViewRequestTypes.myShift === type || (ListViewRequestTypes.openShift === type && x.raw.isAvailableOffer),
            isMyRequest: !x.raw.isAvailableOffer && !NON_REQUEST_CALENDAR_IDS.includes(x.calendarId),
            searchIndex: (`${dateLabel}+${getAsStartAndEndTimeFormat(offeredShift.startDateTime, offeredShift.endDateTime)} ${hasTransfer(originalShift) ? ' (X) ' : ' '} (${getAsLongDate(offeredShift.startDateTime)})+${offeredShift.segments?.map(x => getSegmentPeriodSearchIndex(x)).join(' ')}+${workSegment?.shortOrgJobPath}+${owner}+${requestName}`).toLowerCase(),
        }
        return listItem;
    });
    return [...monthlyScheduleListItems];
}

const getOfferedShift = (scheduleItem) => {
    if (NON_REQUEST_CALENDAR_IDS.includes(scheduleItem.calendarId)) {
        const shift = scheduleItem.raw;
        return { offeredShift: shift, originalShift: shift };
    }
    switch(scheduleItem.raw.type){
        case ListViewRequestTypes.toCover:{
            const shift = scheduleItem.raw.shift;
            const result = { offeredShift: shift, originalShift: shift };
            if (scheduleItem.raw.isPartial) {
                result.offeredShift = scheduleItem.raw.partialShift;
            }
            return result;
        }
        case ListViewRequestTypes.shiftSwap: {
            let originalShift;
            let offeredShift;
            if (scheduleItem.raw.isOwnProposals) {
                originalShift = scheduleItem.raw.proposals[0].shifts[0];
                offeredShift = originalShift;
                if (scheduleItem.raw.proposals[0].isPartial) {
                    originalShift = scheduleItem.raw.proposals[0].partialShift;
                }
            }
            else {
                originalShift = scheduleItem.raw.shifts[0];
                offeredShift = originalShift;
                if (scheduleItem.raw.isPartial) {
                    offeredShift = scheduleItem.raw.partialShift;
                }
            }
            return { offeredShift: offeredShift, originalShift: originalShift };
        }
        case ListViewRequestTypes.openShift: {
            let shift;
            if (scheduleItem.raw.isAvailableOffer) {
                shift = scheduleItem.raw.shifts[0];
            }
            else {
                shift = scheduleItem.raw.shift;
            }
            const result = { offeredShift: shift, originalShift: shift };
            if (scheduleItem.raw.isPartial) {
                result.offeredShift = scheduleItem.raw.partialShift;
            }
            return result;
        }
        case ListViewRequestTypes.voluntaryTimeOff:
            let shift;
            if (!scheduleItem.raw.isAvailableOffer && !!scheduleItem.raw.approvedShifts.length) {
                shift = scheduleItem.raw.approvedShifts[0]?.shiftInfo?.shiftDetails;
            }
            else if (!!scheduleItem.raw.affectedShifts.length) {
                shift = scheduleItem.raw.affectedShifts[0];
            }
            else {
                shift = {
                    startDateTime: scheduleItem.raw.timeOffStartDate,
                    endDateTime: scheduleItem.raw.timeOffEndDate,
                    segments: [],
                }
            }
            return { offeredShift: shift, originalShift: shift };
        default:
            return null;
    }
}

const getOwner = (scheduleItem) => {
    if (scheduleItem.raw.isAvailableOffer) {
        if (scheduleItem.raw.type === ListViewRequestTypes.toCover){
            return scheduleItem.raw.employeeFromName;
        }
        else if (scheduleItem.raw.type === ListViewRequestTypes.shiftSwap) {
            return scheduleItem.raw.offerorEmployeeName;
        }
    }
    return '';
}

const getType = (scheduleItem) => {
    if (scheduleItem.calendarId === "0") {
        return ListViewRequestTypes.myShift;
    }
    else if (scheduleItem.calendarId === "1"){
        return ListViewRequestTypes.myPaycode;
    }
    return scheduleItem.raw.type;
}

const getSubtype = (scheduleItem) => {
    if ([ListViewRequestTypes.openShift, ListViewRequestTypes.voluntaryTimeOff].includes(scheduleItem.raw.type)){
        return scheduleItem.raw.requestSubtype;
    }
    return null;
}

const getLocation = (orgJobPath) => {
    if (!orgJobPath) {
        return '';
    }
    let split = orgJobPath.split("/");
    split = split.slice(0, -1);
    return split.join("/");
}

const getJob = (orgJobPath) => {
    if (!orgJobPath) {
        return '';
    }
    const split = orgJobPath.split("/");
    return split.pop();
}

/**
 * 
 * @param {*[]} monthlySchedule 
 * @param {IListViewItem} rowData 
 */
export const getMonthlyViewOfferModel = (monthlySchedule, rowData) => {
    if (rowData.type === ListViewRequestTypes.openShift) {
        return monthlySchedule.find(x => x.id === rowData.id && x.managerSettingId === rowData.managerSettingId)
    }
    const schedule = monthlySchedule.find(x => x.id === rowData.id)
    return schedule;
};

export const getShiftRowKey = (rowData) => `${rowData.id}_${rowData.managerSettingId}`;
export const getOpenShiftRowKey = (rowData) => `${rowData.key}_${rowData.managerSettingId}`;

function getRequestName(scheduleItem) {
    if (scheduleItem.calendarId === "1") {
        return `${scheduleItem.raw.payCodeRef.qualifier} [${toHours(new Date(scheduleItem.raw.endDateTime) - new Date(scheduleItem.raw.startDateTime))}]`;
    }
    else if (scheduleItem.calendarId === "0" || scheduleItem.raw.isAvailableOffer) {
        return scheduleItem.title;
    }
    return `${scheduleItem.title} ${scheduleItem.raw.managerSettingName}`
}

/**
 * 
 * @param {typeof import('../../../../Enums/RequestType').RequestTypes} type 
 * @param {*} offeredShift 
 * @returns 
 */
function getWorkSegment(type, offeredShift) {
    if (type === ListViewRequestTypes.openShift) {
        return offeredShift.segments?.find(x => x.type !== ShiftSegmentTypeName.BREAK_SEGMENT);
    }
    return offeredShift.segments?.find(x => x.type === ShiftSegmentTypeName.REGULAR_SEGMENT) || offeredShift.segments?.find(x => x.type === ShiftSegmentTypeName.TRANSFER_SEGMENT);
}
